import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useCart } from '../context/CartContext';
import { ordersAPI, walletAPI, API_URL, addressesAPI } from '../lib/api';
import { MapPin, CreditCard, Wallet, ShoppingBag, Plus, Check, X, Home, Briefcase } from 'lucide-react';
import { toast } from 'react-hot-toast';
import PaymentForm from '../components/PaymentForm';

const defaultImages: Record<string, string> = {
    Fruits: 'https://images.unsplash.com/photo-1571771894821-ce9b6c11b08e?w=400',
    Vegetables: 'https://images.unsplash.com/photo-1597362925123-77861d3fbac7?w=400',
    Dairy: 'https://images.unsplash.com/photo-1628088062854-d1870b4553da?w=400',
    Bakery: 'https://images.unsplash.com/photo-1509440159596-0249088772ff?w=400',
    Grains: 'https://images.unsplash.com/photo-1586201375761-83865001e31c?w=400',
    default: 'https://images.unsplash.com/photo-1542838132-92c53300491e?w=400',
};

const Checkout = () => {
    const navigate = useNavigate();
    const { cart, getCartTotal, clearCart } = useCart();
    const [address, setAddress] = useState('');
    const [selectedAddressId, setSelectedAddressId] = useState<string | null>(null);
    const [addresses, setAddresses] = useState<any[]>([]);
    const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState('wallet');
    const [walletBalance, setWalletBalance] = useState(0);
    const [loading, setLoading] = useState(false);



    // Payment State
    const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
    const [paymentIntentId, setPaymentIntentId] = useState<string | null>(null);

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/login');
            return;
        }
        if (cart.length === 0) {
            navigate('/products');
            return;
        }
        fetchWalletBalance();
        fetchAddresses();
    }, []);

    const fetchWalletBalance = async () => {
        try {
            const response = await walletAPI.getBalance();
            setWalletBalance(response.data.balance || 0);
        } catch (error) {
            console.error('Error fetching wallet balance:', error);
        }
    };

    const formatAddress = (addr: any) => {
        return `${addr.fullAddress}, ${addr.city}, ${addr.state} - ${addr.pincode}`;
    };

    const fetchAddresses = async () => {
        try {
            const response = await addressesAPI.getAll();
            setAddresses(response.data || []);
            // Set default address if available
            const defaultAddr = response.data.find((a: any) => a.isDefault);
            if (defaultAddr) {
                setAddress(formatAddress(defaultAddr));
                setSelectedAddressId(defaultAddr._id);
            }
        } catch (error) {
            console.error('Error fetching addresses:', error);
        }
    };

    const handleAddAddressClick = () => {
        if (addresses.length === 0) {
            navigate('/addresses');
        } else {
            setIsAddressModalOpen(true);
        }
    };

    const getProductImage = (product: any) => {
        if (product.images && product.images.length > 0 && product.images[0]) {
            const img = product.images[0];
            if (img.startsWith('/uploads')) {
                return `${API_URL}${img}`;
            }
            return img;
        }
        return defaultImages[product.category || ''] || defaultImages.default;
    };



    const total = getCartTotal();
    const deliveryFee = 0; // FREE
    const grandTotal = total + deliveryFee;

    const handlePlaceOrder = async () => {
        if (!selectedAddressId) {
            toast.error('Please select a delivery address');
            return;
        }

        if (paymentMethod === 'wallet' && walletBalance < grandTotal) {
            toast.error('Insufficient wallet balance');
            return;
        }

        if (paymentMethod === 'card' && !paymentIntentId) {
            setIsPaymentModalOpen(true);
            return;
        }

        setLoading(true);
        try {
            const sId = (cart[0]?.product as any)?.storeId;
            const orderData = {
                items: cart.map(item => ({
                    productId: item.productId,
                    name: item.product.name,
                    quantity: item.quantity,
                    price: item.product.price,
                })),
                totalAmount: grandTotal,
                deliveryAddress: selectedAddressId,
                paymentMethod,
                paymentIntentId: paymentIntentId,

                storeId: typeof sId === 'object' ? sId?._id : sId,
            };

            await ordersAPI.create(orderData);
            clearCart();
            toast.success('Order placed successfully!');
            navigate('/orders');
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to place order');
        } finally {
            setLoading(false);
        }
    };

    const handlePaymentSuccess = (id: string) => {
        setPaymentIntentId(id);
        setIsPaymentModalOpen(false);
        placeOrderWithPayment(id);
    };

    const placeOrderWithPayment = async (pId: string) => {
        setLoading(true);
        try {
            const sId = (cart[0]?.product as any)?.storeId;
            const orderData = {
                items: cart.map(item => ({
                    productId: item.productId,
                    name: item.product.name,
                    quantity: item.quantity,
                    price: item.product.price,
                })),
                totalAmount: grandTotal,
                deliveryAddress: selectedAddressId,
                paymentMethod: 'card',
                paymentIntentId: pId,

                storeId: typeof sId === 'object' ? sId?._id : sId,
            };

            await ordersAPI.create(orderData);
            clearCart();
            toast.success('Order placed successfully!');
            navigate('/orders');
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to place order');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Layout>
            <div className="max-w-6xl mx-auto">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900">Checkout</h1>
                </div>

                <div className="grid lg:grid-cols-3 gap-8">
                    {/* Main Content */}
                    <div className="lg:col-span-2 space-y-8">
                        {/* Address Section */}
                        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                            <div className="flex items-center gap-2 mb-6">
                                <MapPin size={22} className="text-[#10b981]" />
                                <h2 className="text-lg font-bold text-gray-800">Delivery Address</h2>
                            </div>

                            <div
                                className="border-2 border-dashed border-gray-200 rounded-xl p-8 flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-[#10b981] hover:bg-gray-50 transition-all group"
                                onClick={handleAddAddressClick}
                            >
                                <div className="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center group-hover:bg-[#10b981]/10">
                                    <Plus size={24} className="text-gray-400 group-hover:text-[#10b981]" />
                                </div>
                                <span className="text-gray-500 font-medium group-hover:text-[#10b981]">
                                    {addresses.length === 0 ? 'Add New Address' : 'Select from Saved Addresses'}
                                </span>
                            </div>

                            {address && (
                                <div className="mt-4 p-4 bg-green-50 border border-green-100 rounded-xl flex items-start justify-between gap-3">
                                    <div className="flex items-start gap-3">
                                        <Check size={18} className="text-green-600 mt-1" />
                                        <div>
                                            <p className="font-semibold text-green-800">Selected Address</p>
                                            <p className="text-green-700 text-sm">{address}</p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => {
                                            setAddress('');
                                            setSelectedAddressId(null);
                                        }}
                                        className="p-1 hover:bg-green-100 rounded-lg text-green-600 transition-colors"
                                        title="Remove address"
                                    >
                                        <X size={18} />
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Payment Section */}
                        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                            <div className="flex items-center gap-2 mb-6">
                                <CreditCard size={22} className="text-[#10b981]" />
                                <h2 className="text-lg font-bold text-gray-800">Payment Method</h2>
                            </div>

                            <div className="space-y-4">
                                <div
                                    onClick={() => setPaymentMethod('wallet')}
                                    className={`relative flex items-center justify-between p-5 border-2 rounded-2xl cursor-pointer transition-all ${paymentMethod === 'wallet'
                                        ? 'border-[#10b981] bg-[#10b981]/5'
                                        : 'border-gray-100 hover:border-gray-200'
                                        }`}
                                >
                                    <div className="flex items-center gap-4">
                                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${paymentMethod === 'wallet' ? 'bg-[#10b981] text-white' : 'bg-gray-100 text-gray-500'
                                            }`}>
                                            <Wallet size={24} />
                                        </div>
                                        <div>
                                            <p className="font-bold text-gray-800">Wallet</p>
                                            <p className="text-sm text-gray-500">Balance: ₹{walletBalance}</p>
                                        </div>
                                    </div>
                                    {paymentMethod === 'wallet' && (
                                        <div className="w-6 h-6 bg-[#10b981] rounded-full flex items-center justify-center">
                                            <Check size={14} className="text-white" />
                                        </div>
                                    )}
                                </div>

                                <div
                                    onClick={() => setPaymentMethod('card')}
                                    className={`relative flex items-center justify-between p-5 border-2 rounded-2xl cursor-pointer transition-all ${paymentMethod === 'card'
                                        ? 'border-[#10b981] bg-[#10b981]/5'
                                        : 'border-gray-100 hover:border-gray-200'
                                        }`}
                                >
                                    <div className="flex items-center gap-4">
                                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${paymentMethod === 'card' ? 'bg-[#10b981] text-white' : 'bg-gray-100 text-gray-500'
                                            }`}>
                                            <CreditCard size={24} />
                                        </div>
                                        <div>
                                            <p className="font-bold text-gray-800">Credit/Debit Card</p>
                                            <p className="text-sm text-gray-500">Pay securely via Stripe</p>
                                        </div>
                                    </div>
                                    {paymentMethod === 'card' && (
                                        <div className="w-6 h-6 bg-[#10b981] rounded-full flex items-center justify-center">
                                            <Check size={14} className="text-white" />
                                        </div>
                                    )}
                                </div>

                                <div
                                    onClick={() => setPaymentMethod('cod')}
                                    className={`relative flex items-center justify-between p-5 border-2 rounded-2xl cursor-pointer transition-all ${paymentMethod === 'cod'
                                        ? 'border-[#10b981] bg-[#10b981]/5'
                                        : 'border-gray-100 hover:border-gray-200'
                                        }`}
                                >
                                    <div className="flex items-center gap-4">
                                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${paymentMethod === 'cod' ? 'bg-[#10b981] text-white' : 'bg-gray-100 text-gray-500'
                                            }`}>
                                            <ShoppingBag size={24} />
                                        </div>
                                        <div>
                                            <p className="font-bold text-gray-800">Cash on Delivery</p>
                                            <p className="text-sm text-gray-500">Pay when you receive</p>
                                        </div>
                                    </div>
                                    {paymentMethod === 'cod' && (
                                        <div className="w-6 h-6 bg-[#10b981] rounded-full flex items-center justify-center">
                                            <Check size={14} className="text-white" />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Order Summary Sidebar */}
                    <div className="lg:col-span-1">
                        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 sticky top-24">
                            <h2 className="text-lg font-bold text-gray-800 mb-6">Order Summary</h2>

                            <div className="space-y-6 mb-8 max-h-[30vh] overflow-y-auto pr-2 custom-scrollbar">
                                {cart.map((item) => (
                                    <div key={item.productId} className="flex items-center gap-4">
                                        <div className="w-16 h-16 rounded-xl overflow-hidden bg-gray-50 flex-shrink-0">
                                            <img
                                                src={getProductImage(item.product)}
                                                alt={item.product.name}
                                                className="w-full h-full object-cover"
                                            />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-bold text-gray-800 truncate">{item.product.name}</p>
                                            <p className="text-sm text-gray-500">{item.quantity} x ₹{item.product.price}</p>
                                        </div>
                                        <div className="text-right">
                                            <p className="font-bold text-gray-800">₹{item.product.price * item.quantity}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>



                            <div className="space-y-4 pt-6 border-t border-gray-100">
                                <div className="flex justify-between text-gray-600">
                                    <span>Subtotal</span>
                                    <span className="font-semibold text-gray-800">₹{total}</span>
                                </div>
                                <div className="flex justify-between text-gray-600">
                                    <span>Delivery</span>
                                    <span className="font-semibold text-[#10b981]">FREE</span>
                                </div>

                                <div className="flex justify-between items-center pt-4 border-t border-gray-100">
                                    <span className="text-lg font-bold text-gray-900">Total</span>
                                    <span className="text-2xl font-bold text-[#10b981]">₹{grandTotal}</span>
                                </div>
                            </div>

                            <button
                                onClick={handlePlaceOrder}
                                disabled={loading || cart.length === 0 || !selectedAddressId}
                                className="w-full mt-8 bg-[#1e3a5f] text-white py-4 rounded-2xl font-bold hover:bg-[#152a45] disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed transition-all shadow-lg shadow-blue-900/10"
                            >
                                {loading ? 'Processing...' : 'Place Order'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Address Selection Modal */}
            {isAddressModalOpen && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl w-full max-w-lg p-6 shadow-2xl">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-bold text-gray-800">Select Delivery Address</h2>
                            <button onClick={() => setIsAddressModalOpen(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                                <X size={20} />
                            </button>
                        </div>

                        <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2 custom-scrollbar">
                            {addresses.map((addr) => (
                                <div
                                    key={addr._id}
                                    onClick={() => {
                                        setAddress(formatAddress(addr));
                                        setSelectedAddressId(addr._id);
                                        setIsAddressModalOpen(false);
                                    }}
                                    className="p-4 border-2 border-gray-100 rounded-xl cursor-pointer hover:border-[#10b981] hover:bg-[#10b981]/5 transition-all group"
                                >
                                    <div className="flex items-start gap-3">
                                        <div className="mt-1 w-10 h-10 rounded-lg bg-gray-50 flex items-center justify-center group-hover:bg-[#10b981]/10">
                                            {addr.type === 'home' ? <Home size={20} className="text-gray-400 group-hover:text-[#10b981]" /> : <Briefcase size={20} className="text-gray-400 group-hover:text-[#10b981]" />}
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-center justify-between mb-1">
                                                <p className="font-bold text-gray-800 capitalize">{addr.type}</p>
                                                {addr.isDefault && <span className="text-[10px] bg-[#10b981]/10 text-[#10b981] px-2 py-0.5 rounded-full font-bold uppercase">Default</span>}
                                            </div>
                                            <p className="text-sm text-gray-500 leading-relaxed">{addr.fullAddress}, {addr.city}</p>
                                            <p className="text-sm text-gray-500">{addr.state} - {addr.pincode}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}

                            <button
                                onClick={() => navigate('/addresses')}
                                className="w-full py-4 border-2 border-dashed border-gray-200 rounded-xl text-gray-500 font-bold hover:border-[#10b981] hover:text-[#10b981] hover:bg-gray-50 transition-all flex items-center justify-center gap-2 mt-2"
                            >
                                <Plus size={20} />
                                Add New Address
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Payment Modal */}
            {isPaymentModalOpen && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl w-full max-w-lg p-6 shadow-2xl">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-bold text-gray-800">Secure Payment</h2>
                            <button onClick={() => setIsPaymentModalOpen(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                                <X size={20} />
                            </button>
                        </div>

                        <div className="mb-6 bg-blue-50 p-4 rounded-xl text-blue-800 text-sm">
                            <p className="font-bold mb-1">Amount to Pay: ₹{grandTotal}</p>
                            <p>Your payment is secure and encrypted.</p>
                        </div>

                        <PaymentForm
                            amount={grandTotal}
                            onSuccess={handlePaymentSuccess}
                            onCancel={() => setIsPaymentModalOpen(false)}
                        />
                    </div>
                </div>
            )}
        </Layout>
    );
};

export default Checkout;